import type {
  BeadState,
  BossPack,
  SessionCard,
  SessionItemStatus,
  SessionProgressItem,
  SessionStats,
  TrainingResult,
} from './types';

/** Статус item'а с бэка → состояние точки прогресса. to_repeat (промахнулись,
 *  ждёт повтора) рисуем как 'fail' — та же красная точка, что в живом потоке. */
export function beadFromStatus(status: SessionItemStatus): BeadState {
  if (status === 'done') return 'done';
  if (status === 'to_repeat') return 'fail';
  return 'pending';
}

export interface RestoredProgress {
  /** Порядок точек = порядок items по position (как их вернул бэк). */
  itemOrder: string[];
  progressById: Record<string, BeadState>;
  stats: SessionStats;
  /** Была ли хоть одна ошибка — для warn-варианта экрана завершения. */
  requeued: boolean;
  /** itemId карточек, на которых хоть раз ошиблись. Метка не снимается после
   *  верного повтора — итоги показывают «слова с ошибками» за всю тренировку.
   *  При resume восстанавливаем по статусу: to_repeat (промах, ждёт повтора)
   *  или done c attempts>1 (дошли до верного не с первой попытки). */
  mistakeIds: string[];
}

/** Восстанавливает прогресс сессии из per-item данных бэка (для resume).
 *
 *  correct = число DONE (по каждой такой карточке последний ответ верный),
 *  seen    = Σ attempts (сколько всего ответов было),
 *  wrong   = seen − correct (каждый промах — это +1 attempt без DONE).
 *  Это согласовано с живым потоком, где stats инкрементятся по каждому ответу.
 *
 *  Порядок точек: СНАЧАЛА все уже пройденные карточки (done + to_repeat,
 *  attempts>0), ПОТОМ ещё не показанные (pending). Внутри каждой группы — по
 *  position (как пришли с бэка). Это важно для UX: промахнувшаяся карточка
 *  реквьюится ВПЕРЁД по очереди (position растёт), и если сортировать точки
 *  чисто по position, её красная точка «уехала бы» вправо от текущей — будто
 *  ошибка где-то впереди. Группировка «пройденные → новые» держит красную
 *  точку ПОЗАДИ маркера текущей карточки, ровно как в живом потоке (где точки
 *  идут в порядке первого показа), а когда реквьюнутая карточка снова всплывёт,
 *  маркер просто прыгнет к её точке. */
export function restoreProgress(items: SessionProgressItem[]): RestoredProgress {
  const attempted = items.filter((i) => i.status !== 'pending');
  const untouched = items.filter((i) => i.status === 'pending');
  const ordered = [...attempted, ...untouched];

  const itemOrder: string[] = [];
  const progressById: Record<string, BeadState> = {};
  const mistakeIds: string[] = [];
  let correct = 0;
  let seen = 0;
  for (const it of ordered) {
    itemOrder.push(it.itemId);
    progressById[it.itemId] = beadFromStatus(it.status);
    if (it.status === 'done') correct += 1;
    if (it.status === 'to_repeat' || (it.status === 'done' && it.attempts > 1)) {
      mistakeIds.push(it.itemId);
    }
    seen += it.attempts;
  }
  const wrong = Math.max(0, seen - correct);
  return {
    itemOrder,
    progressById,
    stats: { correct, wrong, seen },
    requeued: wrong > 0,
    mistakeIds,
  };
}

/** Итог по карточкам: верно с первой попытки — точка «done» без единой ошибки (после промаха
 *  карточка возвращается с тем же itemId и тоже становится «done», но в mistakeIds остаётся);
 *  отвеченные — все, кроме «pending». total — из сессии: точек может быть меньше, если бэк
 *  не прислал items[]. */
export function summarizeTraining(
  itemOrder: readonly string[],
  progressById: Readonly<Record<string, BeadState>>,
  mistakeIds: readonly string[],
  total: number,
): TrainingResult {
  const mistakes = new Set(mistakeIds);
  let correct = 0;
  let answered = 0;
  for (const id of itemOrder) {
    const bead = progressById[id] ?? 'pending';
    if (bead !== 'pending') answered += 1;
    if (bead === 'done' && !mistakes.has(id)) correct += 1;
  }
  const size = Math.max(total, answered);
  return { correct: Math.min(correct, size), total: size, answered };
}

/** Размер одного boss-пака. Пользователь может пройти несколько паков подряд;
 *  это меньше когнитивной нагрузки на круг, чем 50 карточек одной кучей.
 *  8 подобрано так, чтобы пак (две колонки по 8) ВЛЕЗАЛ в один экран без
 *  скролла (см. компактные размеры .bp-card + height:100dvh у .lx-sess-wrap).
 *  Поэтому 15 карточек = два этапа 8 + 7, а не один длинный список. */
export const BOSS_PACK_SIZE = 8;

/** Fisher-Yates без модификации входного массива. `Math.random()-0.5`-сортировка
 *  даёт смещённое распределение, что для matching-этапа критично — мы не хотим,
 *  чтобы левый столбец стабильно начинался с одного и того же слова. */
function shuffled<T>(arr: readonly T[]): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Дедуплицируем по c.id (для boss-режима это stateId, уникальный per-POS),
 *  режем на паки `BOSS_PACK_SIZE`, в каждом перетасовываем левый и правый
 *  столбцы независимо. Хвост короче `BOSS_PACK_SIZE` остаётся — не выкидываем
 *  (важно для маленьких сессий <10 карточек).
 *
 *  ВАЖНО: НЕ используем cardId для дедупа. У multi-POS слова один cardId
 *  на verb-state и noun-state — это разные boss-айтемы с разными `ru`,
 *  и схлопывать их нельзя. */
export function makeBossPacks(cards: SessionCard[]): BossPack[] {
  const seen = new Set<string>();
  const uniques: SessionCard[] = [];
  for (const c of cards) {
    if (!c || seen.has(c.id)) continue;
    seen.add(c.id);
    uniques.push(c);
  }
  const pool = shuffled(uniques);
  const packs: BossPack[] = [];
  for (let i = 0; i < pool.length; i += BOSS_PACK_SIZE) {
    const slice = pool.slice(i, i + BOSS_PACK_SIZE);
    if (slice.length < 1) break;
    packs.push({
      left: shuffled(slice),
      right: shuffled(slice),
    });
  }
  return packs;
}

/** Части речи, у которых есть короткая подпись (`moduleDetail.words.pos.*`, как в модуле). */
const STAGE_POS = new Set(['noun', 'verb', 'phrasal verb', 'adjective', 'adverb', 'phrase']);

/**
 * Ключ подписи части речи на сцене тренировки и в финальном матче (ТЗ демо 1.8): «сущ.», «глаг.»… вместо
 * сырого «NOUN». `other` и неизвестную не выводим совсем — null.
 */
export function stagePosKey(pos: string | null | undefined): string | null {
  const value = pos?.trim().toLowerCase();
  return value && STAGE_POS.has(value) ? `moduleDetail.words.pos.${value}` : null;
}
